/*
 * Brenory cart: window.Brn.cart API + the site-wide cart drawer / cart page controller.
 *
 * Loaded (defer) by layout/brn.liquid and layout/theme.liquid (Horizon). Self-contained: it does not
 * need brn-ui.js. All cart markup is Liquid (sections/brn-cart-drawer.liquid, sections/brn-main-cart.liquid);
 * after every cart change the sections are re-rendered through the Section Rendering API and their
 * [data-brn-cart-region] parts are swapped in place.
 *
 * API (window.Brn.cart):
 *   add(items, {open})  POST /cart/add.js, ensure shipping protection, re-render, open the drawer.
 *                       items: [{id, quantity, selling_plan, properties}] (one object is accepted too). Rejects with an
 *                       Error whose message is Shopify's description when the add fails.
 *   change(key, qty)    POST /cart/change.js for one line (key = line item key), then ensure + re-render.
 *   refresh()           GET /cart.js, ensure + re-render.
 *   open() / close() / toggle() / isOpen()
 *   ensureProtection()  Same as refresh(); fixes the protection line if needed.
 *   setProtection(on)   The toggle row: remembers the choice (see Shipping protection below) and adds/removes the line.
 *   get()               Last known cart (or a fresh GET /cart.js), no mutation.
 *   idle()              Resolves when no cart request is queued.
 * Events on document: brn:cart:updated {detail: cart from /cart.js, plus cart.brn_item_count},
 *   brn:cart:open, brn:cart:close, brn:cart:error {detail: {message}}.
 * Every [data-brn-cart-count] gets the item count without the protection line.
 *
 * Shipping protection (settings.brn_protection_product): at most one unit of the protection variant is kept
 * in the cart, and only while the rules below say so; otherwise its line is removed.
 * - Subscription protection (its product has a selling plan, picked in Liquid: data-protection-plan on the
 *   cart section, or Brn.protection.sellingPlan from layout/brn.liquid): it is a paid, recurring add-on, so it
 *   is OPT-IN. The toggle row starts OFF and is only shown while the cart holds a subscription line
 *   (data-sub-qty); the line is kept only while the customer switched it on in this browser session AND a
 *   subscription line is in the cart. A one-time cart never gets it. It is always added with its plan.
 * - One-time protection (no plan): kept while the cart holds other items and the customer has not switched
 *   it off (stored in localStorage).
 * All cart mutations run through one serial queue, ensureProtection runs once per
 * queued operation and never triggers itself, so it cannot loop. A failed protection add is
 * remembered for the session and not retried.
 *
 * Horizon integration: a capture-phase click listener on window opens this drawer instead of
 * Horizon's (Horizon's own drawer is no longer rendered), and Horizon's add-to-cart event
 * (shopify:cart:lines-update) triggers a refresh, plus open when Horizon's "Auto-open cart drawer" setting
 * is on. Horizon's cart bubble and header live region are kept in sync.
 */
(function () {
  'use strict';

  var Brn = (window.Brn = window.Brn || {});
  if (Brn.cart && !Brn.cart.stub) return;

  var doc = document;
  var docEl = doc.documentElement;
  var DRAWER_ID = 'BrnCartDrawer';
  var DRAWER_SECTION = 'brn-cart-drawer';
  var OPT_OUT_KEY = 'brn:protection-off';
  var OPT_IN_KEY = 'brn:protection-on';
  var FAIL_KEY = 'brn:protection-failed';
  var HORIZON_TRIGGER = '[aria-controls="cart-drawer"], [data-brn-horizon-cart], [data-testid="cart-drawer-trigger"], header-actions a.action__cart';
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var each = Array.prototype.forEach;

  /* ------------------------------------------------------------------ */
  /* Helpers                                                              */
  /* ------------------------------------------------------------------ */

  function toInt(v) {
    var n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }

  function noop() {}

  function warn(err) {
    if (window.console && console.warn) console.warn('[brn-cart]', err && err.message ? err.message : err);
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function hasModifier(e) {
    return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
  }

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function focusEl(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  function findByAttr(scope, attr, value) {
    var list = scope.querySelectorAll('[' + attr + ']');
    for (var i = 0; i < list.length; i++) {
      if (list[i].getAttribute(attr) === value) return list[i];
    }
    return null;
  }

  // Storage that never throws (private mode, blocked site data); falls back to memory.
  var memory = {};

  function store(type) {
    try {
      var s = window[type];
      s.setItem('__brn', '1');
      s.removeItem('__brn');
      return s;
    } catch (e) {
      return null;
    }
  }

  var local = store('localStorage');
  var session = store('sessionStorage');

  function getFlag(s, key) {
    try {
      if (s) return s.getItem(key);
    } catch (e) {
      /* fall through */
    }
    return Object.prototype.hasOwnProperty.call(memory, key) ? memory[key] : null;
  }

  function setFlag(s, key, value) {
    if (value == null) delete memory[key];
    else memory[key] = value;
    try {
      if (s) {
        if (value == null) s.removeItem(key);
        else s.setItem(key, value);
      }
    } catch (e) {
      /* memory copy is enough */
    }
  }

  /* ------------------------------------------------------------------ */
  /* Config                                                               */
  /* ------------------------------------------------------------------ */

  function routes() {
    var r = Brn.routes || {};
    var shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var base = String(r.root || shopRoot);
    if (base.charAt(base.length - 1) !== '/') base += '/';
    var cart = r.cart || base + 'cart';
    return {
      root: base,
      cart: cart,
      cartJs: cart + '.js',
      add: (r.cart_add || base + 'cart/add') + '.js',
      change: (r.cart_change || base + 'cart/change') + '.js',
      update: (r.cart_update || base + 'cart/update') + '.js'
    };
  }

  function drawerEl() {
    return doc.getElementById(DRAWER_ID);
  }

  function cartRoots() {
    return doc.querySelectorAll('[data-brn-cart-section]');
  }

  function pageRoot() {
    return doc.querySelector('[data-brn-cart-page]');
  }

  function configEl() {
    return drawerEl() || doc.querySelector('[data-brn-cart-section]');
  }

  function protection() {
    var el = configEl();
    var p = Brn.protection || {};
    return {
      variantId: (el && toInt(el.getAttribute('data-protection-variant'))) || toInt(p.variantId),
      productId: (el && toInt(el.getAttribute('data-protection-product'))) || toInt(p.productId),
      sellingPlan: (el && toInt(el.getAttribute('data-protection-plan'))) || toInt(p.sellingPlan),
      // Costs nothing on any delivery: then it is ON by default (opt-out) even with a selling plan.
      free: el && el.hasAttribute('data-protection-free') ? el.getAttribute('data-protection-free') === 'true' : !!p.free
    };
  }

  /** The /cart/add line for one protection unit, with its selling plan when it has one. */
  function protectionLine(prot) {
    var line = { id: prot.variantId, quantity: 1 };
    if (prot.sellingPlan) line.selling_plan = prot.sellingPlan;
    return line;
  }

  function isProtectionItem(item, prot) {
    if (!item || !prot.variantId) return false;
    if (prot.productId && toInt(item.product_id) === prot.productId) return true;
    return toInt(item.variant_id) === prot.variantId;
  }

  function optedOut() {
    return getFlag(local, OPT_OUT_KEY) === '1';
  }

  function setOptOut(off) {
    setFlag(local, OPT_OUT_KEY, off ? '1' : null);
  }

  // Consent to the paid, recurring protection add-on: given with the toggle, kept for this browser session only.
  function optedIn() {
    return getFlag(session, OPT_IN_KEY) === '1';
  }

  function setOptIn(on) {
    setFlag(session, OPT_IN_KEY, on ? '1' : null);
  }

  /**
   * Whether the cart should hold the protection line. s: {other, sub} (summarize() or the server-rendered counts).
   * A subscription protection is opt-in and only rides along with a subscription line; a one-time one is opt-out.
   */
  function wantProtection(s, prot) {
    // A paid subscription protection is opt-in; a free one (or a one-time one) is ON by default (opt-out).
    if (prot.sellingPlan && !prot.free) return s.sub > 0 && optedIn();
    if (prot.sellingPlan) return s.sub > 0 && !optedOut();
    return s.other > 0 && !optedOut();
  }

  function protectionFailed(prot) {
    return !!prot.variantId && getFlag(session, FAIL_KEY) === String(prot.variantId);
  }

  function setProtectionFailed(prot, on) {
    setFlag(session, FAIL_KEY, on ? String(prot.variantId) : null);
  }

  /** Splits a cart into the protection line(s) and everything else (sub = units on a selling plan). */
  function summarize(cart, prot) {
    var s = { other: 0, sub: 0, protQty: 0, protLines: [], keep: null };
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (isProtectionItem(item, prot)) {
        s.protQty += item.quantity;
        s.protLines.push(item);
        if (!s.keep && toInt(item.variant_id) === prot.variantId) s.keep = item;
      } else {
        s.other += item.quantity;
        if (item.selling_plan_allocation) s.sub += item.quantity;
      }
    }
    return s;
  }

  // Last known protection state (from the server-rendered section, then from every committed cart).
  var known = null;

  function readKnown() {
    var el = configEl();
    if (!el || !el.hasAttribute('data-other-qty')) return null;
    return {
      other: toInt(el.getAttribute('data-other-qty')),
      sub: toInt(el.getAttribute('data-sub-qty')),
      protQty: toInt(el.getAttribute('data-prot-qty')),
      protLines: toInt(el.getAttribute('data-prot-lines')),
      protKeep: toInt(el.getAttribute('data-prot-keep'))
    };
  }

  /* ------------------------------------------------------------------ */
  /* Network                                                              */
  /* ------------------------------------------------------------------ */

  function request(url, body) {
    var opts = {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    };
    if (body) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(url, opts).then(function (res) {
      return res.text().then(function (text) {
        var data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch (e) {
          data = null;
        }
        if (!res.ok || data == null) {
          var msg = data && (data.description || data.message);
          var err = new Error(typeof msg === 'string' && msg ? msg : 'Wystąpił błąd koszyka (' + res.status + ')');
          err.status = res.status;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  /** Shopify refused the request (sold out, deleted variant...), as opposed to a network/server hiccup. */
  function isClientError(err) {
    return !!(err && err.status >= 400 && err.status < 500);
  }

  function getCart() {
    return request(routes().cartJs);
  }

  function sectionIds() {
    var ids = [];
    each.call(cartRoots(), function (el) {
      var id = el.getAttribute('data-brn-cart-section');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function sectionsUrl() {
    // A JSON-template section (the cart page) only renders in the context of its own page.
    var onlyDrawer = sectionIds().every(function (id) {
      return id === DRAWER_SECTION;
    });
    return onlyDrawer ? routes().root : window.location.pathname || routes().root;
  }

  /**
   * POSTs a cart mutation and asks Shopify to render our sections in the same response
   * (bundled section rendering). ctx.sections then holds markup that matches the cart right after
   * this mutation; any later mutation replaces it.
   */
  function mutate(url, body, ctx) {
    var ids = sectionIds();
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = sectionsUrl();
    }
    ctx.sections = null;
    return request(url, body).then(function (data) {
      if (data.sections && typeof data.sections === 'object') {
        ctx.sections = data.sections;
        delete data.sections;
      }
      return data;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Serial queue                                                         */
  /* ------------------------------------------------------------------ */

  var queue = Promise.resolve();
  var pending = 0;

  function setBusy(on) {
    each.call(cartRoots(), function (el) {
      el.classList.toggle('is-busy', on);
      if (on) el.setAttribute('aria-busy', 'true');
      else el.removeAttribute('aria-busy');
    });
  }

  /** Runs task() after every earlier cart operation has settled. Returns task()'s promise. */
  function enqueue(task) {
    pending += 1;
    setBusy(true);
    var run = queue.then(task);
    function done() {
      pending -= 1;
      if (pending === 0) setBusy(false);
    }
    queue = run.then(done, done);
    return run;
  }

  function whenIdle() {
    var q = queue;
    return q.then(function () {
      return q === queue ? undefined : whenIdle();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Shipping protection                                                  */
  /* ------------------------------------------------------------------ */

  /**
   * Makes the protection line match the rules for this cart and resolves with the resulting cart.
   * Called exactly once per queued operation (never recursively, never from an event handler).
   */
  function ensureProtection(cart, ctx) {
    var prot = protection();
    if (!prot.variantId || !cart || !cart.items) return Promise.resolve(cart);

    var s = summarize(cart, prot);
    var want = wantProtection(s, prot);
    var updates = {};
    var needUpdate = false;
    for (var i = 0; i < s.protLines.length; i++) {
      var line = s.protLines[i];
      var qty = want && line === s.keep ? 1 : 0;
      if (line.quantity !== qty) {
        updates[line.key] = qty;
        needUpdate = true;
      }
    }
    var needAdd = want && !s.keep && !protectionFailed(prot);
    if (!needUpdate && !needAdd) return Promise.resolve(cart);

    var chain = Promise.resolve(cart);
    if (needUpdate) {
      chain = chain.then(function () {
        return mutate(routes().update, { updates: updates }, ctx);
      });
    }
    if (needAdd) {
      chain = chain.then(function () {
        return mutate(routes().add, { items: [protectionLine(prot)] }, ctx).then(getCart, function (err) {
          // Unavailable or deleted protection product (4xx): remember it and keep the cart usable.
          if (isClientError(err)) setProtectionFailed(prot, true);
          warn(err);
          return getCart();
        });
      });
    }
    return chain.catch(function (err) {
      warn(err);
      ctx.sections = null;
      return getCart();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                            */
  /* ------------------------------------------------------------------ */

  function parseHTML(html) {
    if (window.DOMParser) return new DOMParser().parseFromString(html, 'text/html');
    var div = doc.createElement('div');
    div.innerHTML = html;
    return div;
  }

  function findRoot(scope, id) {
    return findByAttr(scope, 'data-brn-cart-section', id);
  }

  function restoreFocus(el, key) {
    var target = key ? findByAttr(el, 'data-brn-focus', key) : null;
    if (!target || !isVisible(target)) target = el.querySelector('[data-brn-focus-fallback]');
    if (!target) target = el.querySelector('[data-brn-cart-panel]');
    focusEl(target);
  }

  function swap(el, fresh) {
    each.call(fresh.attributes, function (a) {
      if (a.name.indexOf('data-') === 0 && el.getAttribute(a.name) !== a.value) el.setAttribute(a.name, a.value);
    });

    var active = doc.activeElement;
    var hadFocus = !!(active && active !== doc.body && el.contains(active));
    var focusKey = hadFocus ? active.getAttribute('data-brn-focus') : null;

    each.call(fresh.querySelectorAll('[data-brn-cart-region]'), function (fr) {
      var cur = findByAttr(el, 'data-brn-cart-region', fr.getAttribute('data-brn-cart-region'));
      if (!cur) return;
      var scroller = cur.hasAttribute('data-brn-cart-scroll') ? cur : null;
      var top = scroller ? scroller.scrollTop : 0;
      cur.innerHTML = fr.innerHTML;
      if (scroller) scroller.scrollTop = top;
      if (fr.hasAttribute('hidden')) cur.setAttribute('hidden', '');
      else cur.removeAttribute('hidden');
      if (cur.className !== fr.className) cur.className = fr.className;
    });

    if (hadFocus && !el.contains(doc.activeElement)) restoreFocus(el, focusKey);
  }

  function applySections(map, ids) {
    ids.forEach(function (id) {
      var html = map[id];
      if (typeof html !== 'string' || !html) return;
      var fresh = findRoot(parseHTML(html), id);
      if (!fresh) return;
      each.call(cartRoots(), function (el) {
        if (el.getAttribute('data-brn-cart-section') === id) swap(el, fresh);
      });
    });
  }

  function render(ctx) {
    var ids = sectionIds();
    if (!ids.length) return Promise.resolve();
    var bundled =
      ctx &&
      ctx.sections &&
      ids.every(function (id) {
        return typeof ctx.sections[id] === 'string' && ctx.sections[id];
      });
    var source = bundled
      ? Promise.resolve(ctx.sections)
      : request(sectionsUrl() + '?sections=' + encodeURIComponent(ids.join(',')));
    return source.then(function (map) {
      applySections(map, ids);
    });
  }

  /* ------------------------------------------------------------------ */
  /* State, counts, events                                                */
  /* ------------------------------------------------------------------ */

  var state = { cart: null };
  var emitting = false;

  function updateCounts(n) {
    each.call(doc.querySelectorAll('[data-brn-cart-count]'), function (el) {
      el.textContent = n;
      el.hidden = n === 0;
    });
  }

  /** Keeps Horizon's header bubble (snippets/cart-bubble.liquid + assets/cart-icon.js) in sync. */
  function syncHorizonBubble(n) {
    // Horizon's header live region announces cart.totalQuantity (protection line included):
    // correct it, but only after Horizon has announced something (no announcement on page load).
    each.call(doc.querySelectorAll('header-actions [ref="liveRegion"]'), function (region) {
      if (!region.textContent) return;
      var label = (window.Theme && Theme.translations && Theme.translations.cart_count) || 'Liczba produktów w koszyku';
      region.textContent = label + ': ' + n;
    });
    var icons = doc.querySelectorAll('cart-icon');
    if (!icons.length) return;
    setFlag(session, 'cart-count', JSON.stringify({ value: String(n), timestamp: Date.now() }));
    each.call(icons, function (icon) {
      if (typeof icon.renderCartBubble === 'function') {
        try {
          var r = icon.renderCartBubble(n, false);
          if (r && typeof r.catch === 'function') r.catch(noop);
          return;
        } catch (e) {
          /* fall back to the DOM below */
        }
      }
      var count = icon.querySelector('[ref="cartBubbleCount"]');
      var bubble = icon.querySelector('[ref="cartBubble"]');
      if (count) {
        count.textContent = n < 100 ? String(n) : '';
        count.classList.toggle('hidden', n === 0);
      }
      if (bubble) bubble.classList.toggle('visually-hidden', n === 0);
      icon.classList.toggle('header-actions__cart-icon--has-cart', n > 0);
    });
  }

  // Polish plural: 1 produkt, 2-4 produkty (but 12-14 produktów), 5+ produktów.
  function itemsLabel(n) {
    var m10 = n % 10;
    var m100 = n % 100;
    if (n === 1) return '1 produkt';
    return n + (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14) ? ' produkty' : ' produktów');
  }

  function announce(msg) {
    each.call(doc.querySelectorAll('[data-brn-cart-status]'), function (el) {
      el.textContent = msg;
    });
  }

  var errorTimer = 0;

  function clearError() {
    clearTimeout(errorTimer);
    each.call(doc.querySelectorAll('[data-brn-cart-error]'), function (box) {
      box.hidden = true;
      box.textContent = '';
    });
  }

  function showError(msg) {
    msg = msg || 'Coś poszło nie tak. Spróbuj ponownie.';
    each.call(doc.querySelectorAll('[data-brn-cart-error]'), function (box) {
      box.textContent = msg;
      box.hidden = false;
    });
    clearTimeout(errorTimer);
    errorTimer = setTimeout(clearError, 8000);
    doc.dispatchEvent(new CustomEvent('brn:cart:error', { detail: { message: msg } }));
  }

  function emit(cart, s) {
    cart.brn_item_count = s.other;
    updateCounts(s.other);
    syncHorizonBubble(s.other);
    emitting = true;
    try {
      doc.dispatchEvent(new CustomEvent('brn:cart:updated', { detail: cart }));
    } finally {
      emitting = false;
    }
  }

  /** Stores the final cart of an operation, re-renders the sections, then fires brn:cart:updated. */
  function commit(cart, ctx) {
    var s = summarize(cart, protection());
    state.cart = cart;
    known = {
      other: s.other,
      sub: s.sub,
      protQty: s.protQty,
      protLines: s.protLines.length,
      protKeep: s.keep ? s.keep.quantity : 0
    };
    return render(ctx)
      .catch(warn)
      .then(function () {
        emit(cart, s);
        announce('Koszyk zaktualizowany: ' + itemsLabel(s.other));
        return cart;
      });
  }

  /* ------------------------------------------------------------------ */
  /* Operations                                                           */
  /* ------------------------------------------------------------------ */

  function normalizeItems(items) {
    if (!Array.isArray(items)) items = [items];
    var out = [];
    items.forEach(function (it) {
      if (!it) return;
      var id = toInt(it.id || it.variantId || it.variant_id);
      if (!id) return;
      var line = { id: id, quantity: Math.max(1, toInt(it.quantity) || 1) };
      if (it.properties && typeof it.properties === 'object') line.properties = it.properties;
      if (it.selling_plan) line.selling_plan = it.selling_plan;
      out.push(line);
    });
    return out;
  }

  function add(items, opts) {
    opts = opts || {};
    var list = normalizeItems(items);
    if (!list.length) return Promise.reject(new Error('Brak produktów do dodania'));

    return enqueue(function () {
      var ctx = {};
      var R = routes();
      var prot = protection();
      var k = known || readKnown();
      // Send the protection unit in the same request when the cart will want it (saves two round trips).
      // A subscription protection only rides along with a subscription line the customer opted in for.
      var willWant = prot.sellingPlan
        ? (prot.free ? !optedOut() : optedIn()) &&
          list.some(function (it) {
            return !!it.selling_plan && it.id !== prot.variantId;
          })
        : !optedOut();
      var bundleProtection =
        !!prot.variantId &&
        willWant &&
        !protectionFailed(prot) &&
        !!k &&
        k.protQty === 0 &&
        !list.some(function (it) {
          return it.id === prot.variantId;
        });

      var first = bundleProtection
        ? mutate(R.add, { items: list.concat([protectionLine(prot)]) }, ctx).catch(function (err) {
            // Retry without protection; if that works, the protection product was the problem.
            return mutate(R.add, { items: list }, ctx).then(function (res) {
              if (isClientError(err)) setProtectionFailed(prot, true);
              warn(err);
              return res;
            });
          })
        : mutate(R.add, { items: list }, ctx);

      return first
        .then(getCart)
        .then(function (cart) {
          return ensureProtection(cart, ctx);
        })
        .then(function (cart) {
          return commit(cart, ctx);
        });
    }).then(
      function (cart) {
        if (opts.open !== false) open();
        return cart;
      },
      function (err) {
        if (isOpen() || pageRoot()) showError(err && err.message);
        else doc.dispatchEvent(new CustomEvent('brn:cart:error', { detail: { message: err && err.message } }));
        throw err;
      }
    );
  }

  function change(key, qty) {
    key = String(key == null ? '' : key);
    qty = Math.max(0, toInt(qty));
    var prot = protection();
    // Removing the protection line by hand counts as switching it off.
    if (prot.variantId && qty === 0 && key.split(':')[0] === String(prot.variantId)) {
      setOptOut(true);
      setOptIn(false);
    }

    return enqueue(function () {
      var ctx = {};
      return mutate(routes().change, { id: key, quantity: qty }, ctx)
        .then(function (cart) {
          return ensureProtection(cart, ctx);
        })
        .then(function (cart) {
          return commit(cart, ctx);
        })
        .catch(function (err) {
          // Re-sync the UI with the real cart, then report the error.
          return getCart()
            .then(function (cart) {
              return commit(cart, {});
            })
            .catch(noop)
            .then(function () {
              throw err;
            });
        });
    });
  }

  function refresh() {
    if (emitting) return Promise.resolve(state.cart); // never re-enter from a brn:cart:updated listener
    return enqueue(function () {
      var ctx = {};
      return getCart()
        .then(function (cart) {
          return ensureProtection(cart, ctx);
        })
        .then(function (cart) {
          return commit(cart, ctx);
        });
    });
  }

  function setProtection(on) {
    var prot = protection();
    setOptOut(!on);
    setOptIn(!!on);
    if (on) setProtectionFailed(prot, false);
    return refresh();
  }

  /* ------------------------------------------------------------------ */
  /* Drawer                                                               */
  /* ------------------------------------------------------------------ */

  var lastFocus = null;
  var closeTimer = 0;
  var lock = null;

  function isOpen() {
    var el = drawerEl();
    return !!(el && !el.hidden && el.classList.contains('is-open'));
  }

  function lockScroll() {
    if (lock) return;
    var pw = doc.querySelector('.page-wrapper');
    var target = doc.body;
    var gap = window.innerWidth - docEl.clientWidth;
    if (pw && pw.scrollHeight > pw.clientHeight && /(auto|scroll)/.test(getComputedStyle(pw).overflowY)) {
      target = pw; // Horizon desktop: the page wrapper is the scroller
      gap = pw.offsetWidth - pw.clientWidth;
    }
    lock = { target: target, padding: target.style.paddingRight };
    if (gap > 0) target.style.paddingRight = gap + 'px';
    docEl.classList.add('brn-scroll-lock');
  }

  function unlockScroll() {
    docEl.classList.remove('brn-scroll-lock');
    if (lock) {
      lock.target.style.paddingRight = lock.padding;
      lock = null;
    }
  }

  function setExpanded(on) {
    each.call(doc.querySelectorAll(HORIZON_TRIGGER), function (t) {
      if (t.tagName === 'BUTTON') t.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
  }

  function open(opener) {
    var page = pageRoot();
    if (page) {
      // On the cart page the page itself is the cart.
      var title = doc.getElementById('BrnCartPageTitle');
      window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
      if (title) focusEl(title);
      return;
    }
    var el = drawerEl();
    if (!el) {
      window.location.href = routes().cart;
      return;
    }
    if (isOpen()) return;
    clearTimeout(closeTimer);
    var active = opener && opener.nodeType === 1 ? opener : doc.activeElement;
    lastFocus = active && active !== doc.body ? active : null;
    el.hidden = false;
    void el.offsetWidth; // start the slide-in transition from the closed state
    el.classList.add('is-open');
    lockScroll();
    setExpanded(true);
    focusEl(el.querySelector('.brn-cd__close') || el.querySelector('[data-brn-cart-panel]'));
    doc.dispatchEvent(new CustomEvent('brn:cart:open'));
  }

  function close() {
    var el = drawerEl();
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    unlockScroll();
    setExpanded(false);
    clearTimeout(closeTimer);
    closeTimer = setTimeout(
      function () {
        if (!el.classList.contains('is-open')) el.hidden = true;
      },
      reducedMotion() ? 0 : 340
    );
    var back = lastFocus;
    lastFocus = null;
    if (back && back.isConnected && isVisible(back)) focusEl(back);
    doc.dispatchEvent(new CustomEvent('brn:cart:close'));
  }

  function toggle() {
    if (isOpen()) close();
    else open();
  }

  /** Horizon's quick-add modal is a top-layer <dialog>: open our drawer once it has closed. */
  function openAfter(modal) {
    if (modal && modal.open) {
      modal.addEventListener(
        'close',
        function () {
          open();
        },
        { once: true }
      );
      return;
    }
    open();
  }

  /* ------------------------------------------------------------------ */
  /* UI handlers (delegated, work for the drawer and the cart page)       */
  /* ------------------------------------------------------------------ */

  function lineInput(scope, key) {
    var inputs = scope.querySelectorAll('[data-brn-qty-input]');
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].getAttribute('data-key') === key) return inputs[i];
    }
    return null;
  }

  function updateLine(fromEl, key, qty) {
    var line = fromEl.closest('[data-brn-line]');
    if (line) line.classList.add('is-loading');
    clearError();
    change(key, qty).catch(function (err) {
      showError(err && err.message);
    });
  }

  function stepQty(btn) {
    var key = btn.getAttribute('data-key');
    if (!key) return;
    var scope = btn.closest('[data-brn-cart-section]') || doc;
    var input = lineInput(scope, key);
    var current = input ? toInt(input.value) : 0;
    var next = Math.max(0, current + toInt(btn.getAttribute('data-brn-qty-step')));
    var max = input && input.getAttribute('max') ? toInt(input.getAttribute('max')) : 0;
    if (max && next > max) next = max;
    if (next === current) return;
    if (input) input.value = next;
    updateLine(btn, key, next);
  }

  function upsellAdd(btn) {
    var id = toInt(btn.getAttribute('data-variant-id'));
    if (!id || btn.classList.contains('is-loading')) return;
    var line = { id: id, quantity: 1 };
    var plan = toInt(btn.getAttribute('data-selling-plan'));
    if (plan) line.selling_plan = plan; // subscription-only upsell (its terms show under it)
    btn.classList.add('is-loading');
    btn.setAttribute('aria-disabled', 'true');
    clearError();
    add([line], { open: false })
      .catch(noop) // add() already showed the error
      .then(function () {
        if (btn.isConnected) {
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-disabled');
        }
      });
  }

  doc.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var el = t.closest('[data-brn-cart-close]');
    if (el && el.closest('[data-brn-cart-drawer]')) {
      e.preventDefault();
      close();
      return;
    }
    if ((el = t.closest('[data-brn-qty-step]'))) {
      e.preventDefault();
      stepQty(el);
      return;
    }
    if ((el = t.closest('[data-brn-remove]'))) {
      e.preventDefault();
      var key = el.getAttribute('data-key');
      if (key) updateLine(el, key, 0);
      return;
    }
    if ((el = t.closest('[data-brn-upsell-add]'))) {
      e.preventDefault();
      upsellAdd(el);
      return;
    }
    if ((el = t.closest('[data-brn-cart-open]'))) {
      // The Brenory header handles its own cart link (and prevents default); this covers any other opener.
      if (e.defaultPrevented || hasModifier(e) || e.button !== 0) return;
      e.preventDefault();
      open(el);
    }
  });

  doc.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches) return;
    if (t.matches('[data-brn-qty-input]')) {
      var raw = String(t.value).trim();
      if (raw === '') {
        t.value = t.defaultValue;
        return;
      }
      var qty = Math.max(0, toInt(raw));
      var max = t.getAttribute('max') ? toInt(t.getAttribute('max')) : 0;
      if (max && qty > max) qty = max;
      if (String(qty) === t.defaultValue) {
        t.value = t.defaultValue;
        return;
      }
      t.value = String(qty);
      t.defaultValue = String(qty); // a second change event (Enter, then blur) is ignored
      updateLine(t, t.getAttribute('data-key'), qty);
      return;
    }
    if (t.matches('[data-brn-protection-toggle]')) {
      clearError();
      setProtection(t.checked).catch(function (err) {
        showError(err && err.message);
      });
    }
  });

  doc.addEventListener('keydown', function (e) {
    var t = e.target;
    if (e.key === 'Enter' && t && t.matches && t.matches('[data-brn-qty-input]')) {
      e.preventDefault();
      t.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    if (!isOpen()) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    var panel = drawerEl().querySelector('[data-brn-cart-panel]');
    if (!panel) return;
    var items = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (n) {
      return n.getAttribute('tabindex') !== '-1' && isVisible(n);
    });
    if (!items.length) {
      e.preventDefault();
      focusEl(panel);
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var active = doc.activeElement;
    if (!panel.contains(active)) {
      e.preventDefault();
      focusEl(e.shiftKey ? last : first);
    } else if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      focusEl(last);
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      focusEl(first);
    }
  });

  // Checkout waits for queued cart requests (e.g. the protection line) before leaving the page.
  doc.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('[data-brn-checkout-form]')) return;
    var btn = form.querySelector('[type="submit"]');
    if (btn) btn.classList.add('is-loading');
    if (pending === 0) return;
    e.preventDefault();
    whenIdle().then(function () {
      var live = form.isConnected && isVisible(form) ? form : null;
      if (!live) {
        each.call(doc.querySelectorAll('[data-brn-checkout-form]'), function (f) {
          if (!live && isVisible(f)) live = f;
        });
      }
      if (live) HTMLFormElement.prototype.submit.call(live);
      else if (btn) btn.classList.remove('is-loading');
    });
  });

  // Back/forward cache restores a stale page: re-sync.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted && cartRoots().length) {
      each.call(doc.querySelectorAll('[data-brn-checkout-form] .is-loading'), function (b) {
        b.classList.remove('is-loading');
      });
      refresh().catch(warn);
    }
  });

  /* ------------------------------------------------------------------ */
  /* Horizon integration (layout/theme.liquid)                            */
  /* ------------------------------------------------------------------ */

  // Capture phase on window runs before Horizon's declarative on:click handler (document, capture),
  // so Horizon never sees the click and its drawer code never runs.
  window.addEventListener(
    'click',
    function (e) {
      if (e.defaultPrevented || e.button !== 0 || hasModifier(e)) return;
      var t = e.target;
      if (!t || !t.closest) return;
      var trigger = t.closest(HORIZON_TRIGGER);
      if (!trigger || trigger.closest('[data-brn-cart-section]')) return;
      if (!drawerEl() && !pageRoot()) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      open(trigger);
    },
    true
  );

  // Horizon product forms / quick add / Shopify.actions.updateCart fire this after (or while) changing the cart.
  doc.addEventListener('shopify:cart:lines-update', function (e) {
    if (!cartRoots().length) return;
    var action = e.action || (e.detail && e.detail.action);
    var src = e.target && e.target.closest ? e.target : null;
    var modal = null;
    if (src) {
      try {
        modal = src.closest('dialog:modal');
      } catch (err) {
        modal = src.closest('dialog[open]');
      }
    }
    Promise.resolve(e.promise)
      .then(
        function (result) {
          var failed = !!(result && result.detail && result.detail.didError);
          return refresh().then(function () {
            // Respect Horizon's "Auto-open cart drawer" setting (off: only the bubble updates, as before).
            if (action === 'add' && !failed && autoOpen()) openAfter(modal);
          });
        },
        function (err) {
          if (err && err.name === 'AbortError') return undefined;
          return refresh();
        }
      )
      .catch(warn);
  });

  /* ------------------------------------------------------------------ */
  /* Theme editor + init                                                  */
  /* ------------------------------------------------------------------ */

  doc.addEventListener('shopify:section:select', function (e) {
    if (e.detail && e.detail.sectionId === DRAWER_SECTION) open();
  });

  doc.addEventListener('shopify:section:deselect', function (e) {
    if (e.detail && e.detail.sectionId === DRAWER_SECTION) close();
  });

  doc.addEventListener('shopify:section:load', function (e) {
    known = readKnown();
    // The editor replaced the drawer markup: drop the lock of the old (open) drawer.
    if (e.detail && e.detail.sectionId === DRAWER_SECTION && !isOpen()) {
      unlockScroll();
      setExpanded(false);
    }
  });

  function autoOpen() {
    var el = drawerEl();
    return !!(el && el.getAttribute('data-auto-open') === 'true');
  }

  /* Horizon's cart buttons point aria-controls at its removed #cart-drawer: point them at ours. */
  function retargetHorizonTriggers() {
    if (!drawerEl() || doc.getElementById('cart-drawer')) return;
    each.call(doc.querySelectorAll('[aria-controls="cart-drawer"]'), function (t) {
      if (t.closest('[data-brn-cart-section]')) return;
      t.setAttribute('data-brn-horizon-cart', '');
      t.setAttribute('aria-controls', DRAWER_ID);
    });
  }

  function init() {
    retargetHorizonTriggers();
    known = readKnown();
    var prot = protection();
    if (!known || !prot.variantId) return;
    // The server-rendered cart decides whether the protection line needs fixing; no request otherwise.
    var want = wantProtection(known, prot);
    var ok = want
      ? (known.protLines === 1 && known.protKeep === 1 && known.protQty === 1) ||
        (known.protQty === 0 && protectionFailed(prot))
      : known.protQty === 0;
    if (!ok) refresh().catch(warn);
  }

  Brn.cart = {
    stub: false,
    add: add,
    change: change,
    refresh: refresh,
    open: open,
    close: close,
    toggle: toggle,
    isOpen: isOpen,
    ensureProtection: function () {
      return refresh();
    },
    setProtection: setProtection,
    get: function () {
      return state.cart ? Promise.resolve(state.cart) : getCart();
    },
    idle: whenIdle
  };

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
